import { useState } from 'react';
import { Select } from '@mantine/core';
import { useDispatch, useSelector } from 'react-redux';

import {
  setLoadingMessage,
  setSelectedSpeciesModel,
  setShowGlobalLoading,
  speciesModelOptionsSelector,
} from '../../../store/slices/appSlice';
import { tokenSelector } from '../../../store/slices/authSlice';
import { getDataBucketVersions } from '../../../services/aws';
import { NotificationType, showNotification } from '../../../utils/showNotifications';
import { generateLabelString } from '../helpers';
import { URLS } from '../../../config/aws.config';
import WFIDButton from '../../../components/WFIDButton/WFIDButton';

interface IVersionList {
  etag: string;
  isLatest: boolean;
  lastModified: string;
  size: number;
  versionId: string;
}

interface IVersionListSelect extends IVersionList {
  label: string;
  value: string;
}

const PreviousVersionDataModel = () => {
  const speciesModelOptions = useSelector(speciesModelOptionsSelector);
  const token = useSelector(tokenSelector);
  const [fetchingVersionData, setFetchingVersionData] = useState(false);

  const [selectedSpeciesOption, setSelectedSpeciesOption] = useState<any>(null);

  const [versionDataList, setVersionDataList] = useState<IVersionListSelect[]>([]);
  const [selectedVersion, setSelectedVersion] = useState<string | null>(null);

  const dispatch = useDispatch();

  const handleSpeciesChange = async (value: string | null, options: any) => {
    setSelectedSpeciesOption(value);
    if (value && token) {
      dispatch(setShowGlobalLoading(true));
      dispatch(setLoadingMessage(`Getting versions for "${value}"....`));

      setFetchingVersionData(true);
      const key = options.originalFileName;
      const response = await getDataBucketVersions(token, key);

      if (!response.success) {
        // show error message
        setFetchingVersionData(false);
        dispatch(setShowGlobalLoading(false));
        dispatch(setLoadingMessage(''));

        showNotification({
          type: NotificationType.Error,
          title: 'Error',
          message: `There was an error getting the versions for "${value}", please try again`,
        });
        return;
      }

      setFetchingVersionData(false);
      const versions = response.data?.versions as IVersionList[];

      const constructVersionList = versions.map((version) => {
        const label = generateLabelString(version);
        return {
          ...version,
          value: version.versionId,
          label,
        };
      }) as IVersionListSelect[];

      // sort the versions by lastModified date in descending order
      constructVersionList.sort((a, b) => {
        const dateA = new Date(a.lastModified).getTime();
        const dateB = new Date(b.lastModified).getTime();
        return dateB - dateA;
      });

      dispatch(setShowGlobalLoading(false));
      dispatch(setLoadingMessage(''));

      setVersionDataList(constructVersionList);
    }
  };
  const handleDataVersionChange = async (value: string | null) => {
    setSelectedVersion(value);
  };

  const handleUpdateVersionChange = async () => {
    const baseUrl = URLS.restore_to_prev_version;
    const findByLabel = speciesModelOptions.find((item) => item.label === selectedSpeciesOption);
    dispatch(setShowGlobalLoading(true));
    dispatch(setLoadingMessage('Restoring...'));
    const encodedVersionId = encodeURIComponent(selectedVersion as string);
    const encodedFileName = encodeURIComponent(findByLabel?.originalFileName || '');
    const encodedToken = encodeURIComponent(token as string);

    const url = `${baseUrl}?key=${encodedFileName}&token=${encodedToken}&versionId=${encodedVersionId}`;
    try {
      const response = await fetch(url);
      const data = await response.json();
      if (!data?.message) {
        showNotification({
          type: NotificationType.Error,
          title: 'Error',
          message: 'There was an error deleting the data model, please try again',
        });

        dispatch(setShowGlobalLoading(false));
        dispatch(setLoadingMessage(''));
        return;
      }
      showNotification({
        type: NotificationType.Success,
        title: 'Success',
        message: `"${findByLabel?.value}" data model restored successfully`,
      });
      dispatch(setSelectedSpeciesModel(null));
      setSelectedSpeciesOption(null);
      setSelectedVersion(null);
      dispatch(setShowGlobalLoading(false));
      dispatch(setLoadingMessage(''));
    } catch (errr) {
      console.log(errr);
    }
  };

  return (
    <div>
      <div>
        <Select
          w='300px'
          searchable
          value={selectedSpeciesOption}
          label='Select species'
          disabled={fetchingVersionData}
          onChange={handleSpeciesChange}
          placeholder='Select species'
          data={speciesModelOptions}
          clearable
        />
      </div>

      {selectedSpeciesOption && !fetchingVersionData && (
        <div className='SelectContainer'>
          <Select
            w='500px'
            searchable
            value={selectedVersion}
            label='Select version you want to replace with'
            disabled={!selectedSpeciesOption}
            onChange={handleDataVersionChange}
            placeholder='Select version'
            data={versionDataList}
            clearable
          />
        </div>
      )}
      <div className='restore-btn-container'>
        <WFIDButton
          content='Restore'
          disabled={!selectedVersion || !selectedSpeciesOption}
          onClick={handleUpdateVersionChange}
          bgColor='white'
        />
      </div>
    </div>
  );
};

export default PreviousVersionDataModel;
